import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Flame, Users } from 'lucide-react';
import { api, ApiRequestError } from '../lib/api';
import { cn } from '../lib/cn';
import { useTenant } from '../auth/TenantContext';
import { useTenantKey } from '../lib/useTenantKey';
import { PageHeader } from '../components/shell/PageHeader';
import { Card } from '../components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { Sparkline, SparklineLegend } from '../components/ui/Sparkline';
import { INTERACTION_METRICS } from '../components/ui/sparklineMetrics';
import { analyticsAccounts, parseAccountParam, pickAccount } from '../features/analytics/accounts';
import { FollowersBarChart } from '../features/analytics/FollowersBarChart';
import { TopPostsCard } from '../features/analytics/TopPostsCard';
import { PLATFORM_META } from '../features/social/platforms';

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

function parseRange(value: string | null): Range {
  const n = Number(value);
  return (RANGES as readonly number[]).includes(n) ? (n as Range) : 7;
}

/**
 * Analytics — the fuller view behind the Dashboard's Engagement Overview + Account
 * Performance widgets. An account + range filter (both reflected in the URL, so links
 * from the dashboard land pre-filtered) drives an impressions/likes/comments chart, plus
 * the per-account follower chart. Every metric is honest: `null`/`—` and the API's
 * own notices are surfaced rather than faked while the backend is still collecting data.
 *
 * PER ACCOUNT, not per platform: two Facebook Pages are two pills and two bars, each with its own
 * numbers. "All accounts" adds every connected account together and is where the page lands — also
 * for a link from before this, which carries `?platform=`; that is no longer read.
 *
 * This panel plots different lines from the Dashboard's (which keeps engagements + reach), so it
 * gates on `hasInteractions` and passes INTERACTION_METRICS to the shared Sparkline.
 */
export function AnalyticsPage() {
  const { activeTenant } = useTenant();
  const tenantKey = useTenantKey();
  const [params, setParams] = useSearchParams();

  const range = parseRange(params.get('range'));

  /** Set URL params, or remove one with `null`. A leftover `platform` goes on the first change. */
  const patch = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    next.delete('platform');
    for (const [k, v] of Object.entries(updates)) {
      if (v == null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const connectionsQuery = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });
  const accounts = useMemo(
    () => (connectionsQuery.data ? analyticsAccounts(connectionsQuery.data) : undefined),
    [connectionsQuery.data],
  );
  const account = pickAccount(parseAccountParam(params.get('account')), accounts);

  const overview = useQuery({
    queryKey: ['analytics-overview', tenantKey, account ?? 'ALL', range],
    queryFn: ({ signal }) => api.getAnalyticsOverview({ account, range }, signal),
    enabled: tenantKey !== '',
  });

  const platformsQuery = useQuery({
    queryKey: ['analytics-platforms', tenantKey],
    queryFn: ({ signal }) => api.getPlatformPerformance(signal),
    enabled: tenantKey !== '',
  });

  const data = overview.data;
  // Gate on hasInteractions — the flag for THIS panel's metrics — rather than hasData, which is
  // true for a followers-only platform, so a measured "0" is never faked. Note it is deliberately
  // not hasEngagementOrReach: that gates the Dashboard's panel, which plots different lines, and a
  // platform can report one set without the other.
  const showTotals = data?.hasInteractions === true;
  const perfRows = platformsQuery.data ?? [];

  return (
    <>
      <PageHeader
        title="Analytics"
        icon={BarChart3}
        description={`${activeTenant?.name ?? 'Workspace'} · Owned-account performance`}
      />

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap gap-1.5">
          <Pill active={account == null} onClick={() => patch({ account: null })} label="All accounts" />
          {(accounts ?? []).map((a) => {
            const meta = PLATFORM_META[a.platform];
            return (
              <Pill
                key={a.connectionId}
                active={account === a.connectionId}
                onClick={() => patch({ account: a.connectionId })}
                icon={<meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                label={a.accountName}
                title={`${meta.label} - ${a.accountName}`}
              />
            );
          })}
        </div>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <Pill
              key={r}
              active={range === r}
              onClick={() => patch({ range: String(r) })}
              label={`${r} days`}
            />
          ))}
        </div>
      </div>

      {/* Engagement Overview */}
      <Card className="p-5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-tint text-tint-fg">
            <Flame className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </span>
          <h2 className="text-base font-semibold text-ink">Engagement Overview</h2>
          <span className="ml-auto text-xs font-medium uppercase tracking-wide text-ink-faint">
            Last {range} days
          </span>
        </div>

        {overview.isLoading ? (
          <LoadingState label="Loading analytics…" />
        ) : overview.isError ? (
          <ErrorState
            message={
              overview.error instanceof ApiRequestError
                ? overview.error.message
                : 'Could not load analytics.'
            }
            onRetry={() => void overview.refetch()}
          />
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-end gap-8">
              {INTERACTION_METRICS.map((metric) => (
                <div key={metric.key}>
                  <p className="text-3xl font-bold leading-none text-ink">
                    {showTotals && data ? data.totals[metric.key].toLocaleString() : '—'}
                  </p>
                  <p className="mt-1 text-sm text-ink-muted">{metric.label}</p>
                </div>
              ))}
            </div>

            <Sparkline
              series={data?.series ?? []}
              metrics={INTERACTION_METRICS}
              height={200}
              className="mt-4"
            />

            <SparklineLegend
              metrics={INTERACTION_METRICS}
              className="mt-3 border-t border-line-soft pt-3"
            />

            {/* Show the API's notice WHENEVER it sends one, not only when the chart is empty:
                some notices qualify data that is present (e.g. what X's per-day figures mean),
                and those are exactly the ones a reader needs while looking at the lines. */}
            {(!showTotals || data?.notice) && (
              <p className="mt-2 text-xs text-ink-faint">
                {data?.notice ??
                  'Metrics are still being collected — this chart fills in once your connected accounts report data.'}
              </p>
            )}
          </>
        )}
      </Card>

      {/* Top Performing Posts — reads the SAME account + range the page is filtered to, so a post
          in this table is a post inside the bars above it. */}
      <TopPostsCard account={account} range={range} />

      {/* Account Performance */}
      <Card className="mt-4 p-5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-tint text-tint-fg">
            <Users className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </span>
          <h2 className="text-base font-semibold text-ink">Account Performance</h2>
        </div>

        {platformsQuery.isLoading ? (
          <LoadingState label="Loading platforms…" />
        ) : platformsQuery.isError ? (
          <ErrorState
            message={
              platformsQuery.error instanceof ApiRequestError
                ? platformsQuery.error.message
                : 'Could not load platform metrics.'
            }
            onRetry={() => void platformsQuery.refetch()}
          />
        ) : perfRows.length === 0 ? (
          <EmptyState
            title="No platform metrics yet"
            description="Connect social accounts and, once they report, per-account follower counts show up here."
          />
        ) : (
          // `notice` is deliberately NOT rendered here: every one of them explains a missing
          // REACH or ENGAGEMENT metric (Instagram's instagram_manage_insights, X's absent reach,
          // Facebook's deprecated Page insights), and this card shows followers only. An account
          // that reports no followers already says so with an em dash on its own bar.
          <FollowersBarChart rows={perfRows} />
        )}
      </Card>
    </>
  );
}

function Pill({
  active,
  onClick,
  icon,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  label: string;
  /**
   * The fuller name, for an account pill: "<Platform> - <account name>". Also the accessible name,
   * because the platform is otherwise only in the icon, and one brand's accounts on two platforms
   * usually share a name.
   */
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      aria-label={title}
      className={cn(
        'focus-ring inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-action text-action-fg' : 'border border-line-input bg-card text-ink-label hover:bg-hover',
      )}
    >
      {icon}
      {/* An account name can be long; it shortens rather than push the row off the page. */}
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}
